<template>
  <teleport to="body">
    <div v-if="modelValue" class="modal-backdrop fade show" @click="$emit('update:modelValue', false)" />

    <div v-if="modelValue" class="modal fade show d-block" tabindex="-1" role="dialog" style="z-index: 1055;"
      @click.stop>
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header bg-white border-bottom">
            <h5 class="modal-title fw-bold text-dark">
              Ajouter un nouvel acteur
            </h5>
            <button type="button" class="btn-close" aria-label="Fermer" @click="$emit('update:modelValue', false)" />
          </div>

          <Form @submit="onSubmit" :validation-schema="acteurSchema" :initial-values="localForm">
            <div class="modal-body p-4">
              <div class="row g-3">

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">
                    Nom complet <span class="text-danger">*</span>
                  </label>
                  <Field name="nomComplet" type="text" class="form-control" placeholder="Nom complet" />
                  <ErrorMessage name="nomComplet" class="text-danger d-block mt-1" />
                </div>

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">
                    Email <span class="text-danger">*</span>
                  </label>
                  <Field name="email" type="email" class="form-control" placeholder="exemple@email.com" />
                  <ErrorMessage name="email" class="text-danger d-block mt-1" />
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                      Pays <span class="text-danger">*</span>
                    </label>
                    <Field name="pays" v-slot="{ field }">
                      <Multiselect :options="paysOptions" :searchable="true" track-by="label" label="label"
                        v-model="field.value" v-bind="field" placeholder="Sélectionner le pays"
                        @change="onPaysChange" />
                    </Field>
                  </div>
                  <ErrorMessage name="pays" class="text-danger" />
                </div>

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">
                    Téléphone <span class="text-danger">*</span>
                  </label>
                  <Field name="telephone" v-slot="{ field }">
                    <vue-tel-input v-if="selectedCountryCode" :key="selectedCountryCode"
                      placeholder="Entrer le numéro de téléphone" v-model="field.value" v-bind="field"
                      :default-country="selectedCountryCode" mode="international" @validate="validate"
                      class="shadow-none fs-md-15 text-black py-2" />
                    <div v-else class="form-control bg-light text-muted">
                      Veuillez d'abord sélectionner un pays
                    </div>
                  </Field>
                  <ErrorMessage name="telephone" class="text-danger" />
                  <div v-if="telephone && !validPhone" class="text-danger">
                    Veuillez entrer un numéro correct
                  </div>
                 
                </div>

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">
                    IFU <small class="text-muted">(optionnel)</small>
                  </label>
                  <Field name="ifu" type="text" class="form-control" placeholder="Numéro IFU" />
                </div>

                <div class="col-6">
                  <label class="d-block text-black fw-semibold mb-2">
                    RCCM <small class="text-muted">(optionnel)</small>
                  </label>
                  <Field name="rccm" type="text" class="form-control" placeholder="Numéro RCCM" />
                </div>

              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="$emit('update:modelValue', false)">
                Annuler
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving || (telephone && !validPhone)">
                <span v-if="saving">Enregistrement...</span>
                <span v-else>Enregistrer</span>
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue'
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as Yup from 'yup'
import ApiService from '@/services/ApiService'
import { success, error } from '@/utils/utils'
import Multiselect from '@vueform/multiselect'
import { VueTelInput } from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'

export default defineComponent({
  name: 'ActeurModal',

  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
    VueTelInput
  },

  props: {
    modelValue: Boolean,
    fieldToFill: String,
    typeActeurId: { type: String, required: true }
  },

  emits: ['update:modelValue', 'created'],


  setup(props, { emit }) {

    interface PaysOption {
      value: string
      label: string
      iso2: string
    }
    const saving = ref(false)
    const codepay = ref()
    const nationalnumlber = ref()
    const telephone = ref('')
    const validPhone = ref<boolean>(false)
    const numberPhone = ref()
    const selectedCountryCode = ref('')
    const paysOptions = ref<PaysOption[]>([])

    const acteurSchema = Yup.object({
      nomComplet: Yup.string().required('Le nom complet est requis'),
      email: Yup.string().email('Email invalide').required("L'email est requis"),
      pays: Yup.mixed().required("Le pays est requis"),
      telephone: Yup.string().required('Le numéro téléphone est obligatoire'),
      ifu: Yup.string().nullable(),
      rccm: Yup.string().nullable(),
    })

    const localForm = ref({
      nomComplet: '',
      email: '',
      pays: null,
      telephone: '',
      ifu: '',
      rccm: '',
    })


    const fetchPays = async () => {
      try {
        const response = await ApiService.get('/pays')
        const paysData = response.data.data.data

        paysOptions.value = paysData.map((p) => ({
          value: p.id,
          label: p.name_fr,
          iso2: p.iso2_code
        }))


      } catch (error) {
        console.error(' Erreur chargement pays:', error)
      }
    }
    const onPaysChange = (selectedPaysId: any) => {

      const selectedPays = paysOptions.value.find(p => p.value === selectedPaysId)

      if (selectedPays && selectedPays.iso2) {
        const newCountryCode = selectedPays.iso2

        telephone.value = ''
        validPhone.value = false
        codepay.value = ''
        nationalnumlber.value = ''
        numberPhone.value = ''


        selectedCountryCode.value = newCountryCode

      } else {
        console.log(' Pas de selectedPays ou pas de iso2')
        console.log(' paysOptions disponibles:', paysOptions.value)
      }
    }
    function validate(phoneObject) {

      validPhone.value = phoneObject.valid

      if (phoneObject.valid) {
        telephone.value = phoneObject.number
        codepay.value = phoneObject.countryCallingCode
        nationalnumlber.value = phoneObject.nationalNumber
        numberPhone.value = phoneObject.nationalNumber
      } else {
        telephone.value = phoneObject.number || ''
        codepay.value = ''
        nationalnumlber.value = ''
        numberPhone.value = ''
        console.log('Téléphone invalide:', phoneObject.number)
      }
    }

    const onSubmit = async (values: any, { resetForm }: any) => {
      if (!validPhone.value) {
        error('Veuillez entrer un numéro de téléphone valide')
        return
      }

      saving.value = true

      try {
        const payload = {
          ...values,
          typeActeurId: props.typeActeurId,
          telephone: telephone.value,
          paysId: values.pays?.value || values.pays
        }

        console.log('Payload envoyé:', payload)

        const res = await ApiService.post('/acteur', payload)
        const newActeur = res.data.data

        success('Acteur créé avec succès !')

        emit('created', {
          acteur: { label: newActeur.nomComplet, value: newActeur.id },
          field: props.fieldToFill
        })

        resetForm()
        resetAllFields()
        emit('update:modelValue', false)

      } catch (err: any) {
        console.error('Erreur création acteur:', err)

        if (err.response?.status === 409 ||
          err.response?.data?.message?.includes('dupliquée') ||
          err.response?.data?.message?.includes('unique')) {
          error('Cet email ou téléphone est déjà utilisé par un autre acteur')
        } else if (err.response?.data?.message) {
          error(err.response.data.message)
        } else {
          error('Erreur lors de la création de l\'acteur')
        }
      } finally {
        saving.value = false
      }
    }

    const resetAllFields = () => {
      localForm.value = {
        nomComplet: '',
        email: '',
        pays: null,
        telephone: '',
        ifu: '',
        rccm: '',
      }
      telephone.value = ''
      validPhone.value = false
      codepay.value = ''
      nationalnumlber.value = ''
      numberPhone.value = ''
      selectedCountryCode.value = ''
    }

    watch(
      () => props.modelValue,
      (isOpen) => {
        if (isOpen) {
          resetAllFields()
        }
      }
    )

    onMounted(() => {
      fetchPays()
    })

    return {
      saving,
      acteurSchema,
      localForm,
      paysOptions,
      telephone,
      validPhone,
      selectedCountryCode,
      validate,
      onPaysChange,
      onSubmit
    }
  }
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

:deep(.vue-tel-input) {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

:deep(.vue-tel-input:focus-within) {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

:deep(.vti__input) {
  border: none !important;
  outline: none !important;
}
</style>